import React, { useEffect, useRef, useState } from 'react';
import { Skeleton } from '@opendate-app/setlist';

// Narrated loading state for the Smart Suggestions panel: a counter that ticks
// down toward the shortlist, a progress bar, and steps that check off over
// shimmering skeleton rows. The backend can't report per-stage progress, so the
// steps run on a fixed timer and hold on the last step ("Ranking and grouping")
// until the fetch resolves and the parent swaps in the real rows.
const STEPS = [
  { at: 0, label: 'Scanning your artist network', note: 'across your catalog' },
  { at: 1500, label: 'Matching tour routing', note: 'near your venues' },
  { at: 3300, label: 'Checking room fit', note: 'by capacity' },
  { at: 5100, label: 'Comparing to your sellouts', note: 'last 24 months' },
  { at: 6800, label: 'Ranking and grouping', note: '' },
];
const RANKING_AT = STEPS[STEPS.length - 1].at;
const TOTAL = 8400;
const START_COUNT = 54694;
const END_COUNT = 22;
const TICK_MS = 80;
// Cap the bar below 100% while loading — it only fills when real data lands (and
// this component unmounts), so a full bar never lies about being done.
const BAR_CAP = 0.95;

// Skeleton row shapes (per the design mock) — the "why this artist" pill width
// varies so the placeholder reads like real, uneven content.
const SKEL = [
  { why: '62%', local: '72px', cap: '128px', spotify: '96px' },
  { why: '48%', local: '64px', cap: '96px', spotify: '116px' },
  { why: '70%', local: '80px', cap: '112px', spotify: '84px' },
  { why: '54%', local: '68px', cap: '88px', spotify: '124px' },
];
// Kept in sync with SmartSuggestions' COLUMNS so the skeleton lines up with real rows.
const COLUMNS = 'minmax(220px, 2fr) minmax(220px, 3fr) minmax(90px, 1fr) minmax(96px, 1fr) minmax(90px, 1fr) minmax(90px, 1fr)';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SkeletonRows = () => (
  <div className="ss-skel" aria-hidden="true">
    {SKEL.map((s, i) => (
      <div key={i} className="ab-tr ss-skel__row" style={{ gridTemplateColumns: COLUMNS }}>
        <div className="ab-td ss-skel__artist">
          <Skeleton width={36} height={36} radius={8} />
          <Skeleton width="60%" height={12} radius={6} />
        </div>
        <div className="ab-td"><Skeleton width={s.why} height={20} radius={9999} /></div>
        <div className="ab-td"><Skeleton width={64} height={20} radius={9999} /></div>
        <div className="ab-td"><Skeleton width={s.local} height={12} radius={6} /></div>
        <div className="ab-td ab-td--num"><Skeleton width={s.cap} height={12} radius={6} /></div>
        <div className="ab-td ab-td--num"><Skeleton width={s.spotify} height={12} radius={6} /></div>
      </div>
    ))}
  </div>
);

const SuggestionsLoading = () => {
  const reduced = prefersReducedMotion();
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);

  useEffect(() => {
    if (reduced) return undefined;
    startRef.current = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.min(Date.now() - startRef.current, TOTAL));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [reduced]);

  if (reduced) {
    return (
      <div className="ss ss-loading" role="status" aria-live="polite">
        <div className="ss-loading__head">
          <div className="ss-loading__sub">Finding your best matches…</div>
        </div>
        <SkeletonRows />
      </div>
    );
  }

  // Counter resolves to END_COUNT by the time the last step begins, then holds;
  // the bar keeps inching (capped) so the panel still reads as "working".
  const countProgress = Math.min(1, elapsed / RANKING_AT);
  const count = Math.round(START_COUNT - (START_COUNT - END_COUNT) * countProgress);
  const barProgress = Math.min(BAR_CAP, elapsed / TOTAL);
  const activeIdx = STEPS.reduce((acc, step, i) => (elapsed >= step.at ? i : acc), 0);

  return (
    <div className="ss ss-loading" role="status" aria-live="polite">
      <div className="ss-loading__head">
        <div className="ss-loading__count">{count.toLocaleString()}</div>
        <div className="ss-loading__sub">Narrowing to your best matches…</div>
      </div>
      <div className="ss-loading__bar"><span style={{ width: `${barProgress * 100}%` }} /></div>
      <ol className="ss-loading__steps">
        {STEPS.map((step, i) => {
          const done = i < activeIdx; // a later step has started
          const active = i === activeIdx; // the last step stays here until data lands
          return (
            <li key={step.label} className={`ss-step ${done ? 'is-done' : ''} ${active ? 'is-active' : ''}`}>
              <span className="ss-step__mark" aria-hidden="true">{done ? '✓' : ''}</span>
              <span className="ss-step__label">{step.label}</span>
              {step.note && <span className="ss-step__note">{step.note}</span>}
            </li>
          );
        })}
      </ol>
      <SkeletonRows />
    </div>
  );
};

export default SuggestionsLoading;
